import axios from 'axios';
import axiosRetry from 'axios-retry';
import config from '../config';
import { UserPublicDataType } from '../types/authTypes';
import {
  ActionType,
  AnalyticActionResponseListType,
  MonthlyAnalyticDataType,
  RoleActionBreakdownResponseList,
  RoleType,
  UserActionBreakdownResponseList,
  UserMapDataType,
} from '../types/analyticTypes';
import { GetActionLogsParams, ActionLogsResponseListType } from '../types/actionLogsTypes';
import { BuildingDataType } from '../types/buildingTypes';
import {
  PerformPortBounceRequestType,
  PerformMerakiPortBounceRequestType,
  PerformPortFlipRequestType,
  PerformMerakiPortFlipRequestType,
  PortActionBatchResponse,
  DhcpReservationDeletionRequestType,
  BaseDhcpReservationCreateRequestType,
  DhcpReservationCreationResponse,
  DhcpReservationUpdateResponse,
  DhcpReservationConflictResolutionResponse,
  DhcpReservationConflictResolutionRequestType,
  UserListDataType,
  BulkDhcpReservationCreateRequestType,
  DhcpBatchReservationsResponse,
} from '../types/types';

class ApiInstanceClass {
  axiosInstance = axios.create({
    baseURL: config.apiUrl,
  });

  createAxiosInstance(token: string) {
    this.axiosInstance = axios.create({
      withCredentials: true,
      baseURL: config.apiUrl,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  getAxiosInstance() {
    return this.axiosInstance;
  }

  getUserInfo() {
    return ApiInstance.getAxiosInstance()
      .get<UserPublicDataType>('user/info')
      .then((r) => r.data);
  }

  getBuildings() {
    axiosRetry(ApiInstance.getAxiosInstance(), {
      retries: 2,
      retryDelay: (retryCount) => retryCount * 1500,
    });

    try {
      return ApiInstance.getAxiosInstance()
        .get<BuildingDataType[]>('buildings/')
        .then((r) => r.data);
    } finally {
      axiosRetry(ApiInstance.getAxiosInstance(), { retries: 0 });
    }
  }

  getBuildingDevices(building: string | undefined, region: string) {
    axiosRetry(ApiInstance.getAxiosInstance(), {
      retries: 2,
      retryDelay: (retryCount) => retryCount * 1500,
    });

    try {
      return ApiInstance.getAxiosInstance()
        .get('buildings/devices', {
          params: { building, region },
        })
        .then((r) => r.data);
    } finally {
      axiosRetry(ApiInstance.getAxiosInstance(), { retries: 0 });
    }
  }

  getCoreDevices(
    ip_address: string,
    device_type: string,
    building_code: string,
    region: string,
    entity: string,
    unit_name: string
  ) {
    return ApiInstance.getAxiosInstance()
      .get(`buildings/devices/core/${ip_address}`, {
        params: { building_code, region, unit_name, device_type, entity },
      })
      .then((r) => r.data);
  }

  getDistroARP(
    ip_address: string,
    device_type: string,
    building_code: string,
    region: string,
    entity: string,
    unit_name: string
  ) {
    return ApiInstance.getAxiosInstance()
      .get(`buildings/devices/distro/arp/${ip_address}`, {
        params: { building_code, region, unit_name, device_type, entity },
      })
      .then((r) => r.data);
  }

  getSwitch(
    ip_address: string,
    device_type: string,
    building_code: string,
    region: string,
    entity: string,
    unit_name: string,
    api_mode?: boolean | null
  ) {
    return ApiInstance.getAxiosInstance()
      .get(`buildings/devices/switch/${ip_address}`, {
        params: { building_code, region, unit_name, entity, device_type, api_mode },
      })
      .then((r) => r.data);
  }

  getUsersWithRoles() {
    return ApiInstance.getAxiosInstance()
      .get<{ users: UserListDataType }>('management/users')
      .then((r) => r.data);
  }

  getActionAnalytics(
    duration: string | undefined,
    actionTypes: ActionType[] | undefined,
    roleType: RoleType | undefined,
    users: string[] | undefined
  ) {
    return ApiInstance.getAxiosInstance()
      .get<AnalyticActionResponseListType>('management/action-analytics', {
        params: { duration, action_types: actionTypes, role_type: roleType, users: users },
        paramsSerializer: {
          indexes: null,
        },
      })
      .then((r) => r.data);
  }

  getAnnualAnalytic(
    actionTypes: ActionType[] | undefined,
    roleType: RoleType | undefined,
    users: string[] | undefined
  ) {
    return ApiInstance.getAxiosInstance()
      .get<[MonthlyAnalyticDataType]>('management/action-analytics-annual', {
        params: { action_types: actionTypes, role_type: roleType, users: users },
        paramsSerializer: {
          indexes: null,
        },
      })
      .then((r) => r.data);
  }

  getActionAnalyticsByRole(
    duration: string | undefined,
    actionTypes: ActionType[] | undefined,
    roleType: RoleType | undefined,
    users: string[] | undefined
  ) {
    return ApiInstance.getAxiosInstance()
      .get<RoleActionBreakdownResponseList>('management/action-analytics-by-role', {
        params: { duration, action_types: actionTypes, role_type: roleType, users: users },
        paramsSerializer: {
          indexes: null,
        },
      })
      .then((r) => r.data);
  }

  getActionAnalyticsByUser(
    duration: string | undefined,
    actionTypes: ActionType[] | undefined,
    roleType: RoleType | undefined,
    users: string[] | undefined,
    topN: number | undefined
  ) {
    return ApiInstance.getAxiosInstance()
      .get<UserActionBreakdownResponseList>('management/action-analytics-by-user', {
        params: {
          duration,
          action_types: actionTypes,
          role_type: roleType,
          users: users,
          top_n: topN,
        },
        paramsSerializer: {
          indexes: null,
        },
      })
      .then((r) => r.data);
  }

  getActionLogs(params: GetActionLogsParams, signal?: AbortSignal) {
    return ApiInstance.getAxiosInstance()
      .get<ActionLogsResponseListType>('management/action-logs', {
        params: params,
        signal,
      })
      .then((r) => r.data);
  }

  getUsersFromActionLogs() {
    return ApiInstance.getAxiosInstance()
      .get<{ users: UserMapDataType }>('management/action-logs/users')
      .then((r) => r.data);
  }

  performPortBounce(payload: PerformPortBounceRequestType) {
    return ApiInstance.getAxiosInstance()
      .post('action/port-bounce', payload)
      .then((r) => r.data);
  }

  performLimitedPortBounce(payload: PerformPortBounceRequestType) {
    return ApiInstance.getAxiosInstance()
      .post('action/limited-port-bounce', payload)
      .then((r) => r.data);
  }

  performPortBounceMacMini(payload: PerformPortBounceRequestType) {
    return ApiInstance.getAxiosInstance()
      .post('action/port-bounce-mac-mini', payload)
      .then((r) => r.data);
  }

  performMerakiPortBounce(payload: PerformMerakiPortBounceRequestType) {
    return ApiInstance.getAxiosInstance()
      .post<PortActionBatchResponse>('action/port-bounce-meraki', payload)
      .then((r) => r.data);
  }

  performLimitedMerakiPortBounce(payload: PerformMerakiPortBounceRequestType) {
    return ApiInstance.getAxiosInstance()
      .post<PortActionBatchResponse>('action/limited-port-bounce-meraki', payload)
      .then((r) => r.data);
  }

  performPortFlip(payload: PerformPortFlipRequestType) {
    return ApiInstance.getAxiosInstance()
      .post('action/port-flip', payload)
      .then((r) => r.data);
  }

  performLimitedPortFlip(payload: PerformPortFlipRequestType) {
    return ApiInstance.getAxiosInstance()
      .post('action/limited-port-flip', payload)
      .then((r) => r.data);
  }

  performPortFlipMacMini(payload: PerformPortFlipRequestType) {
    return ApiInstance.getAxiosInstance()
      .post('action/port-flip-mac-mini', payload)
      .then((r) => r.data);
  }

  performMerakiPortFlip(payload: PerformMerakiPortFlipRequestType) {
    return ApiInstance.getAxiosInstance()
      .post<PortActionBatchResponse>('action/port-flip-meraki', payload)
      .then((r) => r.data);
  }

  performLimitedMerakiPortFlip(payload: PerformMerakiPortFlipRequestType) {
    return ApiInstance.getAxiosInstance()
      .post<PortActionBatchResponse>('action/limited-port-flip-meraki', payload)
      .then((r) => r.data);
  }

  createDhcpReservation(payload: BaseDhcpReservationCreateRequestType) {
    return ApiInstance.getAxiosInstance()
      .post<DhcpReservationCreationResponse>('dhcp-reservation/create', payload)
      .then((r) => r.data);
  }

  updateDhcpReservation(payload: BaseDhcpReservationCreateRequestType) {
    return ApiInstance.getAxiosInstance()
      .post<DhcpReservationUpdateResponse>('dhcp-reservation/update', payload)
      .then((r) => r.data);
  }

  deleteDhcpReservation(payload: DhcpReservationDeletionRequestType) {
    return ApiInstance.getAxiosInstance()
      .post('dhcp-reservation/delete', payload)
      .then((r) => r.data);
  }

  resolveDhcpReservationConflict(payload: DhcpReservationConflictResolutionRequestType) {
    return ApiInstance.getAxiosInstance()
      .post<DhcpReservationConflictResolutionResponse>('dhcp-reservation/resolve-conflict', payload)
      .then((r) => r.data);
  }

  createBulkDhcpReservation(payload: BulkDhcpReservationCreateRequestType) {
    return ApiInstance.getAxiosInstance()
      .post<DhcpBatchReservationsResponse>('dhcp-reservation/batch-create', payload)
      .then((r) => r.data);
  }
}

export const ApiInstance = new ApiInstanceClass();
